// src/components/SimpleComponents/SimpleSegmentIndustry.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Select } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import translateX from 'utils/translateX';
import useFormElement from '../../hooks/useFormElement';
import { defaultSelectFilterOption } from 'utils/helpers';
import { useSimpleFormContext } from './SimpleFormProvider';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// ISIC Rev.4 - International Standard Industrial Classification (UN)
// Section codes (A-U) aligned with CNAE Brazilian classification
// Based on: https://unstats.un.org/unsd/classifications/Family/Detail/27
const ISIC_SECTIONS = [
  { code: 'A', key: 'agriculture' },
  { code: 'B', key: 'mining' },
  { code: 'C', key: 'manufacturing' },
  { code: 'D', key: 'electricity_gas' },
  { code: 'E', key: 'water_waste' },
  { code: 'F', key: 'construction' },
  { code: 'G', key: 'trade_vehicles' },
  { code: 'H', key: 'transportation' },
  { code: 'I', key: 'accommodation_food' },
  { code: 'J', key: 'information_communication' },
  { code: 'K', key: 'financial_insurance' },
  { code: 'L', key: 'real_estate' },
  { code: 'M', key: 'professional_scientific' },
  { code: 'N', key: 'administrative_services' },
  { code: 'O', key: 'public_administration' },
  { code: 'P', key: 'education' },
  { code: 'Q', key: 'health_social' },
  { code: 'R', key: 'arts_entertainment' },
  { code: 'S', key: 'other_services' },
  { code: 'T', key: 'household_employers' },
  { code: 'U', key: 'extraterritorial' },
];

// *******************************************************************************
// FUNCTIONS
// *******************************************************************************
function $buildOptions() {
  return ISIC_SECTIONS.map(({ code, key }) => ({
    value: code, // Use ISIC section code (A-U) as value
    text: translateX(`segments.${key}`),
    label: translateX(`segments.${key}`),
    disabled: false,
  }));
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleSegmentIndustry = ({
  name = 'segment',
  subdocumentField,
  options = $buildOptions(),
  showSearch = true,
  allowClear = true,
  disabled,
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // ANTD 6 FIX: Control dropdown open state to prevent re-render interference
  const [open, setOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // ANTD 6 FIX: Explicitly close dropdown on select to avoid race condition with Form.useWatch
  const handleSelect = useCallback(() => {
    setOpen(false);
  }, []);

  const handleDropdownVisibleChange = useCallback(visible => {
    setOpen(visible);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem {...props} name={fullFieldName} elementProps={elementProps}>
      <Select
        ref={elementRef}
        id={flattenFieldName}
        filterOption={defaultSelectFilterOption}
        options={options}
        showSearch={showSearch}
        allowClear={allowClear}
        disabled={isDisabled}
        // ANTD 6 FIX: Controlled open state to prevent re-render interference
        open={open}
        onSelect={handleSelect}
        onOpenChange={handleDropdownVisibleChange}
      />
    </SimpleFormItem>
  );
};

export default SimpleSegmentIndustry;
